import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { AlertTriangle, Clock, TrendingDown, CreditCard, Play, CheckCircle2 } from "lucide-react";
import { fmtDate } from "@/lib/format";

/**
 * Phase 4 · PO Violations report.
 * Delivery-overdue · Short-supply · Payment-overdue.
 * Sweep is manual for now (button on the tab). Can be scheduled via cron.
 */
export default function POViolations() {
  const [rows, setRows] = useState([]);
  const [sweeping, setSweeping] = useState(false);

  const load = () => api.get("/po-violations").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const sweep = async () => {
    setSweeping(true);
    try {
      const r = await api.post("/po-violations/sweep");
      toast.success(`Scanned ${r.data.scanned} POs · ${r.data.violations_found} violation(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sweep failed"); }
    setSweeping(false);
  };

  const ack = async (id) => {
    try { await api.post(`/po-violations/${id}/acknowledge`); load(); }
    catch (e) { toast.error("Ack failed"); }
  };

  const byKind = {
    delivery_overdue: rows.filter((r) => r.kind === "delivery_overdue"),
    short_supply:     rows.filter((r) => r.kind === "short_supply"),
    payment_overdue:  rows.filter((r) => r.kind === "payment_overdue"),
  };

  const iconFor = (k) => {
    if (k === "delivery_overdue") return <Clock className="w-4 h-4 text-amber-600" />;
    if (k === "short_supply")     return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <CreditCard className="w-4 h-4 text-red-700" />;
  };

  return (
    <div className="space-y-4" data-testid="po-violations-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">// PO violations watchdog</div>
          <div className="text-sm text-muted-foreground">Delivery overdue · Short supply · Payment overdue</div>
        </div>
        <Button onClick={sweep} disabled={sweeping} className="rounded-sm gap-1 bg-accent hover:bg-accent/90 text-white" data-testid="pov-sweep">
          <Play className="w-3.5 h-3.5" /> {sweeping ? "Scanning…" : "Run scan now"}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { k: "delivery_overdue", label: "Delivery overdue",  tone: "bg-amber-500/10 border-amber-500/30 text-amber-800" },
          { k: "short_supply",     label: "Short supply",       tone: "bg-red-500/10 border-red-500/30 text-red-800" },
          { k: "payment_overdue",  label: "Payment overdue",    tone: "bg-red-500/15 border-red-500/40 text-red-900" },
        ].map((c) => (
          <Card key={c.k} className={`rounded-sm border p-3 ${c.tone}`} data-testid={`pov-kpi-${c.k}`}>
            <div className="flex items-center gap-2">{iconFor(c.k)}<div className="text-[10px] font-mono uppercase">{c.label}</div></div>
            <div className="text-3xl font-bold tabular-nums mt-1">{byKind[c.k].length}</div>
          </Card>
        ))}
      </div>

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead>
            <tr>
              <th>Detected</th><th>Kind</th><th>PO #</th><th>Vendor</th><th>Detail</th><th>Severity</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-muted-foreground">No violations. Click "Run scan now" to check.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`pov-row-${r.id}`}>
                <td className="text-xs">{fmtDate(r.detected_at?.slice(0, 10))}</td>
                <td className="text-[10px] font-mono uppercase flex items-center gap-1.5">{iconFor(r.kind)}{r.kind.replaceAll("_", " ")}</td>
                <td className="font-mono text-xs">{r.po_number}</td>
                <td>{r.vendor_name || "—"}</td>
                <td className="text-xs">{r.detail}</td>
                <td>
                  <span className={`text-[10px] font-mono uppercase ${r.severity === "high" ? "text-red-700" : "text-amber-700"}`}>{r.severity}</span>
                </td>
                <td className="text-right">
                  {!r.acknowledged && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => ack(r.id)} data-testid={`pov-ack-${r.id}`}>
                      <CheckCircle2 className="w-3 h-3" /> Ack
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
